import React, { useEffect } from 'react';
import { Admin, Login, useTranslate } from 'react-admin';
import { createTheme } from '@material-ui/core/styles';
import './styles.scss';
import { Resource } from '@react-admin/ra-rbac';
import {
  dataProvider,
  i18nProvider,
  authProvider,
} from '../../utils/providers';
import messages from './messages';
import Dashboard from '../../components/Dashboard';
import CustomLayout from '../../components/Layout';
import DraftEditPage from '../MassTests/DraftEditPage';
import {
  PRESCRIPTIONS_MODULE,
  USERS_MODULE,
  TESTS_MODULE,
  TEST_ASSIGNMENT_MODULE,
  REVIEWED_TESTS_MODULE,
  IMMUNOTHERAPY_MODULE,
  REVIEWED_IMMUNOTHERAPY_MODULE,
  FERTILITY_CHECK_MODULE,
  REVIEWED_FERTILITY_CHECK_MODULE,
  WEIGHT_LOSS_TREATMENT_CHECK_MODULE,
  REVIEWED_WEIGHT_LOSS_TREATMENT_CHECK_MODULE,
  HEALTH_CHECK_BASIC_MODULE,
  REVIEWED_HEALTH_CHECK_BASIC_MODULE,
  CARDIOVASCULAR_AND_DIABETES_RISK_CHECK_MODULE,
  REVIEWED_CARDIOVASCULAR_AND_DIABETES_RISK_CHECK_MODULE,
  TREATMENTS_MODULE,
  REVIEWED_TREATMENTS_MODULE,
} from '../../constants/apiUrls';
import PrescriptionList from '../Prescriptions/PrescriptionList';
import PrescriptionShow from '../Prescriptions/PrescriptionShow';
import UsersList from '../Users/UsersList';
import UsersCreate from '../Users/UsersCreate';
import TestsList from '../Tests/TestsList';
import TestsShow from '../Tests/TestsShow';
import ReviewedTestsShow from '../Tests/ReviewedTestsShow';
import ImmunotherapyTestsList from '../Immunotherapy/ImmunotherapyTestsList';
import ImmunotherapyTestsShow from '../Immunotherapy/ImmunotherapyTestsShow';
import ReviewedImmunotherapyTestsList from '../ReviewedImmunotherapy/ReviewedImmunotherapyTestsList';
import ReviewedImmunotherapyTestShow from '../ReviewedImmunotherapy/ReviewedImmunotherapyTestShow';
import TestAssignmentList from '../TestAssignment/TestAssignmentList';
import TestAssignmentCreate from '../TestAssignment/TestAssignmentCreate';
import FertilityCheckList from '../FertilityCheck/FertilityCheckList';
import FertilityCheckShow from '../FertilityCheck/FertilityCheckShow';
import ReviewedFertilityCheckList from '../ReviewedFertilityCheck/ReviewedFertilityCheckList';
import ReviewedFertilityCheckShow from '../ReviewedFertilityCheck/ReviewedFertilityCheckShow';
import WeightLossTreatmentCheckList from '../WeightLossTreatmentCheck/WeightLossTreatmentCheckList';
import WeightLossTreatmentCheckShow from '../WeightLossTreatmentCheck/WeightLossTreatmentCheckShow';
import ReviewedWeightLossTreatmentCheckList from '../ReviewedWeightLossTreatmentCheck/ReviewedWeightLossTreatmentCheckList';
import ReviewedWeightLossTreatmentCheckShow from '../ReviewedWeightLossTreatmentCheck/ReviewedWeightLossTreatmentCheckShow';
import HealthCheckBasicList from '../HealthCheckBasic/HealthCheckBasicList';
import {
  HealthCheckBasicShow,
  HealthCheckBasicShowReadOnly,
} from '../HealthCheckBasic/HealthCheckBasicShow';
import CardiovascularAndDiabetesRiskCheckList from '../CardiovascularAndDiabetesRiskCheck/CardiovascularAndDiabetesRiskCheckList';
import {
  CardiovascularAndDiabetesRiskCheckShow,
  CardiovascularAndDiabetesRiskCheckShowReadOnly,
} from '../CardiovascularAndDiabetesRiskCheck/CardiovascularAndDiabetesRiskCheckShow';
import TreatmentsList from '../Treatments/TreatmentsList';
import TreatmentsShow from '../Treatments/TreatmentsShow';
import ReviewedTreatmentsList from '../ReviewedTreatments/ReviewedTreatmentsList';
import ReviewedTreatmentsShow from '../ReviewedTreatments/ReviewedTreatmentsShow';

const theme = createTheme({
  palette: {
    text: {
      secondary: 'rgba(102, 102, 102, 1)',
    },
    secondary: {
      main: 'rgba(97, 65, 155, 1)',
    },
    background: { default: '#fff', paper: '#fff' },
  },
});

const App = () => {
  useEffect(() => {
    document.title = 'Health Yourself | Work-with';
  }, []);

  return (
    <Admin
      theme={theme}
      // @ts-ignore
      dataProvider={dataProvider}
      authProvider={authProvider}
      i18nProvider={i18nProvider}
      loginPage={Login}
      layout={CustomLayout}
      dashboard={Dashboard}
    >
      <Resource name="drafts" edit={DraftEditPage} />
      <Resource
        name={USERS_MODULE}
        list={UsersList}
        create={UsersCreate}
        options={{
          label: useTranslate()(messages.usersModule),
        }}
      />
      <Resource
        name={PRESCRIPTIONS_MODULE}
        list={PrescriptionList}
        show={PrescriptionShow}
        options={{
          label: useTranslate()(messages.prescriptionModule),
        }}
      />
      <Resource
        name={TEST_ASSIGNMENT_MODULE}
        list={TestAssignmentList}
        create={TestAssignmentCreate}
        options={{
          label: useTranslate()(messages.testAssignmentModule),
        }}
      />
      <Resource
        name={TESTS_MODULE}
        list={TestsList}
        show={TestsShow}
        options={{
          label: useTranslate()(messages.testsModule),
        }}
      />
      <Resource
        name={REVIEWED_TESTS_MODULE}
        list={TestsList}
        show={ReviewedTestsShow}
        options={{
          label: useTranslate()(messages.reviewedTestsModule),
        }}
      />
      <Resource
        name={IMMUNOTHERAPY_MODULE}
        list={ImmunotherapyTestsList}
        show={ImmunotherapyTestsShow}
        options={{
          label: useTranslate()(messages.immunotherapyModule),
        }}
      />
      <Resource
        name={REVIEWED_IMMUNOTHERAPY_MODULE}
        list={ReviewedImmunotherapyTestsList}
        show={ReviewedImmunotherapyTestShow}
        options={{
          label: useTranslate()(messages.reviewedImmunotherapyModule),
        }}
      />
      <Resource
        name={FERTILITY_CHECK_MODULE}
        list={FertilityCheckList}
        show={FertilityCheckShow}
        options={{
          label: useTranslate()(messages.fertilityCheckModule),
        }}
      />
      <Resource
        name={REVIEWED_FERTILITY_CHECK_MODULE}
        list={ReviewedFertilityCheckList}
        show={ReviewedFertilityCheckShow}
        options={{
          label: useTranslate()(messages.reviewedFertilityCheckModule),
        }}
      />
      <Resource
        name={WEIGHT_LOSS_TREATMENT_CHECK_MODULE}
        list={WeightLossTreatmentCheckList}
        show={WeightLossTreatmentCheckShow}
        options={{
          label: useTranslate()(messages.weightLossTreatmentCheckModule),
        }}
      />
      <Resource
        name={REVIEWED_WEIGHT_LOSS_TREATMENT_CHECK_MODULE}
        list={ReviewedWeightLossTreatmentCheckList}
        show={ReviewedWeightLossTreatmentCheckShow}
        options={{
          label: useTranslate()(
            messages.reviewedWeightLossTreatmentCheckModule,
          ),
        }}
      />
      <Resource
        name={HEALTH_CHECK_BASIC_MODULE}
        list={HealthCheckBasicList}
        show={HealthCheckBasicShow}
        options={{
          label: useTranslate()(messages.healthCheckBasicModule),
        }}
      />
      <Resource
        name={REVIEWED_HEALTH_CHECK_BASIC_MODULE}
        list={HealthCheckBasicList}
        show={HealthCheckBasicShowReadOnly}
        options={{
          label: useTranslate()(messages.reviewedHealthCheckBasicModule),
        }}
      />
      <Resource
        name={CARDIOVASCULAR_AND_DIABETES_RISK_CHECK_MODULE}
        list={CardiovascularAndDiabetesRiskCheckList}
        show={CardiovascularAndDiabetesRiskCheckShow}
        options={{
          label: useTranslate()(
            messages.cardiovascularAndDiabetesRiskCheckModule,
          ),
        }}
      />
      <Resource
        name={REVIEWED_CARDIOVASCULAR_AND_DIABETES_RISK_CHECK_MODULE}
        list={CardiovascularAndDiabetesRiskCheckList}
        show={CardiovascularAndDiabetesRiskCheckShowReadOnly}
        options={{
          label: useTranslate()(
            messages.reviewedCardiovascularAndDiabetesRiskCheckModule,
          ),
        }}
      />
      <Resource
        name={TREATMENTS_MODULE}
        list={TreatmentsList}
        show={TreatmentsShow}
        options={{
          label: useTranslate()(messages.treatmentsModule),
        }}
      />
      <Resource
        name={REVIEWED_TREATMENTS_MODULE}
        list={ReviewedTreatmentsList}
        show={ReviewedTreatmentsShow}
        options={{
          label: useTranslate()(messages.reviewedTreatmentsModule),
        }}
      />
    </Admin>
  );
};

export default App;
